import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  Chip,
  Stack,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PsychologyIcon from '@mui/icons-material/Psychology';
import CurrencyBitcoinIcon from '@mui/icons-material/CurrencyBitcoin';
import IntegrationInstructionsIcon from '@mui/icons-material/IntegrationInstructions';
import ForumIcon from '@mui/icons-material/Forum';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useLanguage } from '../contexts/LanguageContext';
import { Reveal, SpotCard, Glow, GridTexture, BRAND_GRAD } from './premium';

const ServicesSection: React.FC = () => {
  const { t, language } = useLanguage();
  const navigate = useNavigate();

  const packageIcons = [
    <PsychologyIcon />,
    <CurrencyBitcoinIcon />,
    <IntegrationInstructionsIcon />
  ];

  return (
    <Box id="services" sx={{ position: 'relative', overflow: 'hidden', py: { xs: 9, md: 13 }, backgroundColor: '#F6FAFF' }}>
      <Glow color="#6DABE4" top="-8%" left="-8%" size={480} opacity={0.13} />
      <GridTexture opacity={0.045} />
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 8 } }}>
          <Typography
            variant="h2"
            sx={{
              mb: 3,
              fontSize: { xs: '2rem', md: '2.5rem' },
              fontWeight: 300,
              color: '#37474F',
              textWrap: 'balance',
            }}
          >
            {t.services.title}
          </Typography>
          <Typography
            variant="h6"
            sx={{
              color: '#78909C',
              fontWeight: 400,
              fontSize: { xs: '1rem', md: '1.25rem' },
              maxWidth: '600px',
              mx: 'auto',
            }}
          >
            {t.services.subtitle}
          </Typography>
        </Box>

        {/* rootBot — flagship product spotlight */}
        <Card
          sx={{
            mb: { xs: 6, md: 8 },
            p: { xs: 3, md: 5 },
            borderRadius: '20px',
            border: 'none',
            color: '#fff',
            background: 'linear-gradient(135deg,#13204a,#3b3f8f)',
            overflow: 'hidden',
            position: 'relative',
          }}
        >
          <Grid container spacing={3} alignItems="center">
            <Grid size={{ xs: 12, md: 8 }}>
              <Chip
                label={language === 'th' ? 'สินค้าเรือธง · Flagship' : 'Flagship Product'}
                size="small"
                sx={{ mb: 2, color: '#fff', fontWeight: 600, background: 'rgba(255,255,255,0.14)', border: '1px solid rgba(255,255,255,0.22)' }}
              />
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
                <Box sx={{ width: 44, height: 44, borderRadius: '12px', background: 'linear-gradient(135deg,#0ea5e9,#6366f1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <ForumIcon sx={{ color: '#fff' }} />
                </Box>
                <Typography sx={{ fontWeight: 600, fontSize: { xs: '1.6rem', md: '2rem' } }}>rootBot</Typography>
              </Stack>
              <Typography sx={{ fontWeight: 500, fontSize: { xs: '1.05rem', md: '1.25rem' }, mb: 1.5, textWrap: 'balance' }}>
                {language === 'th'
                  ? 'แพลตฟอร์มแชตบอท AI แบบ White-Label สำหรับองค์กร'
                  : 'White-Label Enterprise AI Chatbot Platform'}
              </Typography>
              <Typography sx={{ color: 'rgba(255,255,255,0.8)', fontSize: { xs: '0.9rem', md: '1rem' }, lineHeight: 1.7, mb: 3, maxWidth: 620, textWrap: 'pretty' }}>
                {language === 'th'
                  ? 'หนึ่งแพลตฟอร์ม รองรับได้ทุกองค์กร — แบรนด์ ช่องทาง ความรู้ และ AI ของคุณเอง แยกขาด 100% ใช้งานจริงแล้วในหน่วยงานรัฐ'
                  : 'One platform for every organization — your own brand, channels, knowledge and AI, 100% isolated. Already in production at government agencies.'}
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 3 }}>
                {['Multi-tenant', 'Pool / Silo', 'RAG · Real-time', 'Google Cloud', 'OCPB · PDMO · NIA'].map((tag) => (
                  <Chip key={tag} label={tag} size="small" sx={{ color: '#c7d2fe', background: 'rgba(255,255,255,0.08)', fontWeight: 500 }} />
                ))}
              </Stack>
              <Button
                onClick={() => { navigate('/rootbot'); window.scrollTo({ top: 0 }); }}
                endIcon={<ArrowForwardIcon />}
                sx={{ background: 'linear-gradient(135deg,#0ea5e9,#6366f1)', color: '#fff', px: 3.5, py: 1.3, fontWeight: 600, borderRadius: '10px', '&:hover': { opacity: 0.92 } }}
              >
                {language === 'th' ? 'ดูรายละเอียด rootBot' : 'Explore rootBot'}
              </Button>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <Stack spacing={1.5}>
                {[
                  { v: '24/7', l: language === 'th' ? 'ตอบทุกช่องทางอัตโนมัติ' : 'Automated, every channel' },
                  { v: '100%', l: language === 'th' ? 'แยกข้อมูลแต่ละองค์กร' : 'Data isolation per org' },
                  { v: 'Realtime', l: language === 'th' ? 'เพิ่มความรู้ ไม่ต้องเทรน' : 'Add knowledge, no retrain' },
                ].map((k) => (
                  <Box key={k.l} sx={{ p: 2, borderRadius: '12px', background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)' }}>
                    <Typography sx={{ fontWeight: 700, fontSize: '1.3rem', color: '#7dd3fc', lineHeight: 1 }}>{k.v}</Typography>
                    <Typography sx={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.82rem', mt: 0.5 }}>{k.l}</Typography>
                  </Box>
                ))}
              </Stack>
            </Grid>
          </Grid>
        </Card>

        <Reveal>
        <Grid container spacing={{ xs: 3, md: 4 }}>
          {t.services.packages.map((pkg, index) => (
            <Grid size={{ xs: 12, md: 6, lg: 4 }} key={index}>
              <SpotCard
                sx={{
                  height: '100%',
                  p: { xs: 2, md: 3 },
                  borderRadius: '18px',
                  border: '1px solid #E6EEF9',
                  boxShadow: '0 12px 34px -18px rgba(0,85,184,0.16)',
                  '&::after': {
                    content: '""', position: 'absolute', top: 0, left: 0, right: 0,
                    height: '4px', background: BRAND_GRAD, zIndex: 2,
                  },
                }}
              >
                <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                  <Box sx={{ textAlign: 'center', mb: 3 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        width: 80,
                        height: 80,
                        mx: 'auto',
                        mb: 3,
                        backgroundColor: 'rgba(0, 85, 184, 0.08)',
                        borderRadius: 2,
                        color: '#0055B8',
                        '& svg': {
                          fontSize: '2.5rem',
                        }
                      }}
                    >
                      {packageIcons[index]}
                    </Box>
                    <Typography
                      variant="h5"
                      sx={{
                        mb: 2,
                        fontWeight: 600,
                        color: '#1A2332',
                        fontSize: { xs: '1.25rem', md: '1.5rem' },
                      }}
                    >
                      {pkg.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#4A5568',
                        fontSize: { xs: '0.875rem', md: '1rem' },
                        lineHeight: 1.6,
                        mb: 3,
                      }}
                    >
                      {pkg.description}
                    </Typography>
                  </Box>

                  <List sx={{ p: 0 }}>
                    {pkg.features.map((feature, featureIndex) => (
                      <ListItem key={featureIndex} sx={{ px: 0, py: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <CheckCircleIcon
                            sx={{
                              color: '#0055B8',
                              fontSize: { xs: '1rem', md: '1.25rem' },
                            }}
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={feature}
                          primaryTypographyProps={{
                            fontSize: { xs: '0.8rem', md: '0.875rem' },
                            color: '#4A5568',
                            fontWeight: 500,
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </SpotCard>
            </Grid>
          ))}
        </Grid>
        </Reveal>
      </Container>
    </Box>
  );
};

export default ServicesSection;